/*
 * gg-header-price.js — Gummy Gardens, 2026-09-20
 *
 * Shows the real price under the product title: the list price struck through, the discounted total, and the
 * dollars saved, matching whichever purchase option is selected.
 *
 * Needed because the one-time multipack saving is delivered by Shopify AUTOMATIC discounts (5% on 2-packs, 10% on
 * 3-packs, one-time orders only). Automatic discounts apply in the cart and are invisible on a product page, so
 * without this the title price contradicts what the shopper is actually charged.
 *
 * SCOPE — deliberately narrow. This ONLY writes to the theme's own price element. It never writes anything inside
 * Recharge's subscription widget. An earlier version did, and it broke the widget: Recharge renders with lit, which
 * tracks its bindings with markers inside its nodes, so writing text or appending children corrupted them and
 * selecting a purchase option stopped working on exactly the packs that were touched. The widget is READ here (to
 * learn which option is selected) and never modified. Reading cannot break it.
 *
 * Rates must match Shopify's automatic discounts and the Recharge plans. If either changes, change them here.
 */
(function () {
  'use strict';

  var ONETIME = { '2PK': 0.05, '3PK': 0.10 };
  var SUB     = { '1PK': 0.10, '2PK': 0.15, '3PK': 0.20 };
  var TAG = 'gg-hdr-saving';
  var DETAIL = 'gg-onetime-detail';
  var BADGE = 'gg-onetime-badge';
  var SUBBADGE = 'gg-sub-badge';
  var ONEPRICE = 'gg-onetime-price';
  var GREEN = 'rgb(15,134,106)';   // Recharge's own badge green, so the savings read as one system

  function money(n) {
    var t = n.toFixed(2);
    return '$' + (t.slice(-3) === '.00' ? t.slice(0, -3) : t);
  }
  function round2(n) { return Math.round(n * 100) / 100; }

  function listPrice() {
    var el = document.querySelector('.price-item--regular');
    var m = ((el || {}).textContent || '').replace(/,/g, '').match(/\$\s*([\d.]+)/);
    return m ? parseFloat(m[1]) : null;
  }

  function pack() {
    var c = document.querySelector('.product-form__input input[type="radio"]:checked[value="1PK"], .product-form__input input[type="radio"]:checked[value="2PK"], .product-form__input input[type="radio"]:checked[value="3PK"]');
    return c ? c.value : null;
  }

  /** READ ONLY. Walks Recharge's open shadow roots to see which option is selected. Never writes. */
  function subscribeSelected() {
    var host = document.querySelector('recharge-subscription-widget');
    if (!host || !host.shadowRoot) return true;            // widget absent or not ready: assume the default
    var stack = [host.shadowRoot], guard = 0;
    while (stack.length && guard++ < 200) {
      var node = stack.shift(), els;
      try {
        var blk = node.querySelector('.rc-purchase-option__subscription');
        if (blk) {
          var radio = blk.querySelector('input[type="radio"]');
          if (radio) return !!radio.checked;
        }
        els = node.querySelectorAll('*');
      } catch (e) { continue; }
      for (var i = 0; i < els.length; i++) if (els[i].shadowRoot) stack.push(els[i].shadowRoot);
    }
    return true;
  }

  /** READ ONLY. Find an element inside Recharge's nested shadow roots. Never writes. */
  function findInWidget(selector) {
    var host = document.querySelector('recharge-subscription-widget');
    if (!host || !host.shadowRoot) return null;
    var stack = [host.shadowRoot], guard = 0;
    while (stack.length && guard++ < 200) {
      var node = stack.shift(), els;
      try {
        var hit = node.querySelector(selector);
        if (hit) return hit;
        els = node.querySelectorAll('*');
      } catch (e) { continue; }
      for (var i = 0; i < els.length; i++) if (els[i].shadowRoot) stack.push(els[i].shadowRoot);
    }
    return null;
  }

  /**
   * Float the saving badge over the one-time option, mirroring where Recharge puts its own.
   * It lives in the light DOM and is positioned from the option's measured rect, so the widget is never modified.
   * pointer-events is none: Recharge's own badge sits over its option and outside the <label>, which is why it
   * swallowed taps and made Subscribe unselectable. This one can never intercept a tap.
   */
  function paintBadge(id, selector, label) {
    var existing = document.getElementById(id);
    var box = label ? findInWidget(selector) : null;
    if (!box) { if (existing) existing.remove(); return; }
    // If Recharge already renders a badge on this option, sit exactly on top of it and match its width, so ours
    // covers theirs completely. Ours can be shorter than theirs ("Save 15% ($12)" vs "Save up to 15%"), so
    // matching the width matters — without it theirs peeks out from behind.
    var theirs = box.querySelector('.rc-purchase-option__badge');
    var r = (theirs ? theirs : box).getBoundingClientRect();
    if (!r.width) { if (existing) existing.remove(); return; }
    var coverWidth = theirs ? Math.ceil(r.width) : 0;
    var el = existing;
    if (!el) {
      el = document.createElement('div');
      el.id = id;
      document.body.appendChild(el);
    }
    el.textContent = label;
    // Values copied from Recharge's own badge (.rc-purchase-option__badge) as computed on this theme, so the two
    // read as one design rather than an approximation: 12px Barlow 400, 0.63px tracking, 6px padding, 4px radius,
    // #0f866a, and the same -11px / 13px offsets from the option's top-right corner.
    el.setAttribute('style', [
      'position:absolute',
      'z-index:5',
      'pointer-events:none',
      'background:' + GREEN,
      'color:#fff',
      'border-radius:4px',
      'padding:6px',
      'font-family:Barlow,sans-serif',
      'font-size:12px',
      'font-weight:400',
      'line-height:12px',
      'letter-spacing:0.63px',
      'white-space:nowrap',
      coverWidth ? ('min-width:' + coverWidth + 'px') : '',
      'box-sizing:border-box',
      'text-align:center',
      'left:' + Math.round(r.right + window.scrollX - (theirs ? 0 : 13)) + 'px',
      'top:' + Math.round(r.top + window.scrollY - (theirs ? 0 : 11)) + 'px',
      'transform:translateX(-100%)'
    ].join(';'));
  }


  /**
   * Cover Recharge's one-time price with the real one, so the option agrees with the price under the title.
   * Recharge prints the raw list price there ($120.00) because the one-time saving comes from a Shopify automatic
   * discount it knows nothing about. Painted over from the light DOM — the widget is still never written to.
   */
  function paintOneTimePrice(list, total) {
    var existing = document.getElementById(ONEPRICE);
    var box = (total != null) ? findInWidget('.rc-selection__onetime') : null;
    var theirPrice = box ? box.querySelector('.rc-price') : null;
    if (!theirPrice) { if (existing) existing.remove(); return; }

    var r = theirPrice.getBoundingClientRect();
    if (!r.width) { if (existing) existing.remove(); return; }

    var el = existing;
    if (!el) {
      el = document.createElement('div');
      el.id = ONEPRICE;
      document.body.appendChild(el);
    }
    el.innerHTML = '';
    var cs = getComputedStyle(theirPrice);
    // Match the surrounding surface so their figure is hidden rather than showing through.
    var bg = 'rgb(255,255,255)';
    var walk = theirPrice.parentElement, guard = 0;
    while (walk && guard++ < 6) {
      var c = getComputedStyle(walk).backgroundColor;
      if (c && c !== 'rgba(0, 0, 0, 0)' && c !== 'transparent') { bg = c; break; }
      walk = walk.parentElement;
    }
    var strike = document.createElement('span');
    strike.setAttribute('style', 'text-decoration:line-through;opacity:.6;margin-right:6px');
    strike.textContent = money(list);
    var now = document.createElement('span');
    now.textContent = money(total);
    el.appendChild(strike);
    el.appendChild(now);
    el.setAttribute('style', [
      'position:absolute',
      'z-index:4',
      'pointer-events:none',
      'background:' + bg,
      'color:' + cs.color,
      'font-family:' + cs.fontFamily,
      'font-size:' + cs.fontSize,
      'font-weight:' + cs.fontWeight,
      'line-height:' + cs.lineHeight,
      'white-space:nowrap',
      'left:' + Math.round(r.left + window.scrollX) + 'px',
      'top:' + Math.round(r.top + window.scrollY) + 'px',
      'min-width:' + Math.ceil(r.width) + 'px',
      'min-height:' + Math.ceil(r.height) + 'px'
    ].join(';'));
  }

  function render() {
    var el = document.querySelector('.price-item--regular');
    if (!el) return;
    var holder = el.parentElement || el;

    var old = holder.querySelectorAll('.' + TAG);
    for (var i = 0; i < old.length; i++) old[i].remove();
    el.style.textDecoration = '';
    el.style.opacity = '';

    var list = listPrice();
    var p = pack();
    if (!list || !p) return;

    var oneRate = ONETIME[p] || 0;
    var rate = subscribeSelected() ? (SUB[p] || 0) : oneRate;

    if (rate) {
      var total = round2(list * (1 - rate));
      el.style.textDecoration = 'line-through';
      el.style.opacity = '0.6';
      var span = document.createElement('span');
      span.className = TAG;
      span.setAttribute('style', 'margin-left:8px;font-weight:700');
      var totalPart = document.createElement('span');
      totalPart.textContent = money(total);
      var savePart = document.createElement('span');
      savePart.setAttribute('style', 'color:' + GREEN + ';margin-left:6px');
      savePart.textContent = '(save ' + money(round2(list - total)) + ')';
      span.appendChild(totalPart);
      span.appendChild(savePart);
      holder.appendChild(span);
    }

    // The one-time multipack saving is the fact the page otherwise hides entirely, because an automatic discount
    // only appears in the cart. Stated here, under the price, rather than inside Recharge's widget — writing in
    // there is what broke option selection before.
    if (oneRate) {
      var oneTotal = round2(list * (1 - oneRate));
      // Nothing is written under the title beyond the price itself — the saving is shown on the option it belongs to.
      paintBadge(BADGE, '.rc-selection__onetime', 'Save ' + Math.round(oneRate * 100) + '% (' + money(round2(list - oneTotal)) + ')');
    } else {
      paintBadge(BADGE, '.rc-selection__onetime', null);
    }
    paintOneTimePrice(list, oneRate ? oneTotal : null);

    // Same treatment for the subscribe badge, so both read in dollars. Recharge's own badge is left untouched and
    // simply covered: ours always carries the extra " ($24)" so it is wider and hides theirs completely.
    var subRate = SUB[p] || 0;
    if (subRate) {
      paintBadge(SUBBADGE, '.rc-purchase-option__subscription',
                 'Save ' + Math.round(subRate * 100) + '% (' + money(round2(list * subRate)) + ')');
    } else {
      paintBadge(SUBBADGE, '.rc-purchase-option__subscription', null);
    }
  }

  var pending = null;
  function schedule() { clearTimeout(pending); pending = setTimeout(render, 250); }

  window.addEventListener('scroll', function () { paintBadgeReposition(); }, { passive: true });
  window.addEventListener('resize', schedule);
  function paintBadgeReposition() {
    var a = document.getElementById(BADGE);
    if (a) paintBadge(BADGE, '.rc-selection__onetime', a.textContent);
    var b = document.getElementById(SUBBADGE);
    if (b) paintBadge(SUBBADGE, '.rc-purchase-option__subscription', b.textContent);
    schedule();   // the one-time price overlay is rebuilt from current values
  }

  document.addEventListener('change', schedule, true);
  document.addEventListener('click', schedule, true);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
  else schedule();
  [600, 1500, 2600, 4000, 6000].forEach(function (ms) { setTimeout(schedule, ms); });
})();
